import { CopyIcon } from "../../../../assets/icons";
import { VendingStatus } from "../../../../model/charge";
import { TRANSACTION_STATUS, TransactionChannelMap, TransactionStatusMap, VendingStatusMap, vendingStatusClassName } from "../../../../model/transaction";
import { handleCopy } from "../../../../utils";
import Button from "../button/Button";
import styles from "./TransactionDetail.module.scss";
import { TransactionDetailData, TransactionDetailType } from "./types";

interface TransactionOverviewProps {
	transaction: TransactionDetailData;
	transactionType: TransactionDetailType;
	onRevend?: () => void;
	onNotifyBilling?: () => void;
	onVerifyVendingToken?: () => void;
	onVerifyPaymentStatus?: () => void;
	reVendLoading?: boolean;
	notifyBillingLoading?: boolean;
	verifyingVendingToken?: boolean;
	verifyingPayment?: boolean;
}

const TransactionOverview = ({
	transaction,
	transactionType,
	onRevend,
	onNotifyBilling,
	onVerifyVendingToken,
	onVerifyPaymentStatus,
	reVendLoading,
	notifyBillingLoading,
	verifyingVendingToken,
	verifyingPayment,
}: TransactionOverviewProps) => {
	const attempts = transaction.attempts ?? 0;
	const failed = transaction.failed ?? 0;

	return (
		<>
			<div className={styles.detailRow}>
				<div className={styles.label}>Payment Status</div>
				<div className={styles.value}>
					<div className={styles.statusContainer}>
						<div className={`${styles.statusIndicator} ${styles[TransactionStatusMap[transaction.paymentStatus].toLowerCase()]}`}></div>
						<div className={`${styles.status}`}>{TransactionStatusMap[transaction.paymentStatus]}</div>
						{transaction.paymentStatus === TRANSACTION_STATUS.IN_PROGRESS && onVerifyPaymentStatus && (
							<Button disabled={verifyingPayment} text={verifyingPayment ? "Verifying" : "Verify"} onClick={onVerifyPaymentStatus} variant="outline" />
						)}
					</div>
				</div>
			</div>

			{transactionType === "vending" && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Vending Status</div>
					<div className={styles.value}>
						<div className={styles.vendingStatusValue}>
							<div className={styles.statusContainer}>
								<div className={`${styles.statusIndicator} ${styles[vendingStatusClassName(transaction?.vendingStatus ?? 0)]}`}></div>
								<div className={`${styles.status}`}>{VendingStatusMap[transaction?.vendingStatus ?? 0] ?? "Unknown"}</div>
							</div>
							{(attempts > 0 || failed > 0) && (
								<div className={styles.attemptsSummary}>
									{attempts > 0 && <span>{attempts} Attempts</span>}
									{failed > 0 && <span className={styles.failedText}>{failed} Failed</span>}
								</div>
							)}
						</div>
						{transaction.paymentStatus === TRANSACTION_STATUS.COMPLETED &&
							(transaction.vendingStatus === VendingStatus.FAILED ||
								transaction.vendingStatus === VendingStatus.UNKNOWN ||
								transaction.vendingStatus === VendingStatus.FAILED_WITH_REFUND) &&
							onRevend && <Button disabled={reVendLoading} text={reVendLoading ? "Revending" : "Revend"} onClick={onRevend} variant="outline" />}
						{transaction.paymentStatus === TRANSACTION_STATUS.COMPLETED && transaction.vendingStatus === TRANSACTION_STATUS.IN_PROGRESS && onVerifyVendingToken && (
							<Button disabled={verifyingVendingToken} text={reVendLoading ? "Verifying" : "Verify Token"} onClick={onVerifyVendingToken} variant="outline" />
						)}
					</div>
				</div>
			)}

			<div className={styles.detailRow}>
				<div>
					<div className={styles.label}>Transaction ID</div>
					<span style={{ marginTop: 8, display: "inline-block" }}>{transaction.transactionId}</span>
				</div>
				<div className={styles.value}>
					<Button text="Copy" onClick={() => handleCopy(transaction.transactionId)} rightIcon={<CopyIcon height={14} style={{ marginLeft: 4 }} />} variant="outline" />
				</div>
			</div>

			<div className={styles.detailRow}>
				<div>
					<div className={styles.label}>Reference</div>
					<span style={{ marginTop: 8, display: "inline-block" }}>{transaction.reference}</span>
				</div>
				<div className={styles.value}>
					<Button text="Copy" onClick={() => handleCopy(transaction.reference)} rightIcon={<CopyIcon height={14} style={{ marginLeft: 4 }} />} variant="outline" />
				</div>
			</div>

			{transactionType === "billing" && (
				<div className={styles.detailRow}>
					<div>
						<div className={styles.label}>Last Attempt Reference</div>
						<span style={{ marginTop: 8, display: "inline-block" }}>{transaction.lastAttemptReference || "—"}</span>
					</div>
					<div className={styles.value}>
						{transaction.lastAttemptReference && (
							<Button
								text="Copy"
								onClick={() => handleCopy(transaction.lastAttemptReference!)}
								rightIcon={<CopyIcon height={14} style={{ marginLeft: 4 }} />}
								variant="outline"
							/>
						)}
					</div>
				</div>
			)}

			{transactionType === "vending" && (
				<div className={styles.detailRow}>
					<div>
						<div className={styles.label}>Remote Token Load Reference</div>
						<span style={{ marginTop: 8, display: "inline-block" }}>{transaction.remoteTokenLoadReference || "—"}</span>
					</div>
					<div className={styles.value}>
						{transaction.remoteTokenLoadReference && (
							<Button
								text="Copy"
								onClick={() => handleCopy(transaction.remoteTokenLoadReference!)}
								rightIcon={<CopyIcon height={14} style={{ marginLeft: 4 }} />}
								variant="outline"
							/>
						)}
					</div>
				</div>
			)}

			{transactionType === "billing" && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Postpaid Status</div>
					<div className={styles.value}>
						<div className={styles.vendingStatusValue}>
							<div className={styles.statusContainer}>
								<div
									className={`${styles.statusIndicator} ${
										transaction?.billingStatus ? styles[TransactionStatusMap[2].toLowerCase()] : styles[TransactionStatusMap[3].toLowerCase()]
									}`}
								></div>
								<div className={`${styles.status}`}>{transaction.billingStatus ? "Notified" : "Not-Notified"}</div>
							</div>
							{(attempts > 0 || failed > 0) && (
								<div className={styles.attemptsSummary}>
									{attempts > 0 && <span>{attempts} Attempts</span>}
									{failed > 0 && <span className={styles.failedText}>{failed} Failed</span>}
								</div>
							)}
						</div>
						{transaction.paymentStatus === TRANSACTION_STATUS.COMPLETED && !transaction.billingStatus && onNotifyBilling && (
							<Button disabled={notifyBillingLoading} text={notifyBillingLoading ? "Notifying" : "Notify Billing"} onClick={onNotifyBilling} variant="outline" />
						)}
					</div>
				</div>
			)}
			{transaction.meterNumber && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Meter Number</div>
					<div className={styles.value}>{transaction.meterNumber}</div>
				</div>
			)}
			{transaction.accountNumber && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Account Number</div>
					<div className={styles.value}>{transaction.accountNumber}</div>
				</div>
			)}
			{transaction.address && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Address</div>
					<div className={styles.value}>{transaction.address}</div>
				</div>
			)}
			{transaction.phoneNumber && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Phone Number</div>
					<div className={styles.value}>{transaction.phoneNumber}</div>
				</div>
			)}
			{transaction.outstandingBalance && (
				<div className={styles.detailRow}>
					<div className={styles.label}>Previus outstanding balance</div>
					<div className={styles.value}>{transaction.outstandingBalance}</div>
				</div>
			)}

			<div className={styles.detailRow}>
				<div className={styles.label}>Payment Channel</div>
				<div className={styles.value}>{TransactionChannelMap[transaction.paymentChannel]}</div>
			</div>
			<div className={styles.detailRow}>
				<div className={styles.label}>District</div>
				<div className={styles.value}>{transaction.district}</div>
			</div>

			{transaction.vendingStatus && (
				<div className={styles.detailRow}>
					<div>
						<div className={styles.label}>Token</div>
						<span style={{ marginTop: 8, display: "inline-block" }}>{transaction.token || "—"}</span>
					</div>
					<div className={styles.value}>
						{transaction.token && (
							<Button text="Copy" onClick={() => handleCopy(transaction.token)} rightIcon={<CopyIcon height={14} style={{ marginLeft: 4 }} />} variant="outline" />
						)}
					</div>
				</div>
			)}

			{transactionType === "vending" && (
				<div className={styles.detailRow}>
					<div>
						<div className={styles.label}>MAP Refund Token</div>
						<span style={{ marginTop: 8, display: "inline-block" }}>{transaction.mapRefundToken || "—"}</span>
					</div>
					<div className={styles.value}>
						{transaction.mapRefundToken && (
							<Button
								text="Copy"
								onClick={() => handleCopy(transaction.mapRefundToken!)}
								rightIcon={<CopyIcon height={14} style={{ marginLeft: 4 }} />}
								variant="outline"
							/>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default TransactionOverview;
